import { GlobalErrorFallback } from '@/components/system/global-error-fallback'
import { QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider, createRouter } from '@tanstack/react-router'
import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'

import { AppToaster } from '@/components/layout/app-toaster'
import { ThemeProvider } from '@/components/layout/theme-provider'
import { initWebVitals } from '@/lib/monitoring/web-vitals'
import { queryClient } from '@/lib/query/client'
import { routeTree } from './routeTree.gen'

// Self-hosted fonts (cyrillic + latin) bundled by Vite — replaces the
// Google Fonts CDN <link> previously in index.html. Each weight ships
// as a separate woff2 chunk; HTTP/2 pushes them in parallel.
import '@fontsource/inter/400.css'
import '@fontsource/inter/500.css'
import '@fontsource/inter/600.css'
import '@fontsource/inter/700.css'
import '@fontsource/inter/800.css'
import '@fontsource/inter/900.css'
import '@fontsource/jetbrains-mono/400.css'
import '@fontsource/jetbrains-mono/500.css'
import '@fontsource/jetbrains-mono/600.css'
import '@fontsource/jetbrains-mono/700.css'

import './index.css'

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  scrollRestoration: true,
  // Fallback error UI for all routes that don't set their own errorComponent
  // (e.g. projects/index, login). The _layout.tsx already sets its own
  // GlobalErrorFallback for project-scoped routes.
  defaultErrorComponent: ({ error, reset }) => <GlobalErrorFallback error={error} reset={reset} />,
  // File-based routes end with '/' (e.g. createFileRoute('/projects/')).
  // Enforce trailing slashes everywhere so /projects hard-navigates to /projects/
  // instead of falling through to the 404 component.
  trailingSlash: 'always',
  // Enable View Transitions API globally for all navigations (T5.1).
  // CSS animation defaults (200ms crossfade) live in index.css; reduced-motion
  // is also handled there. Individual <Link> components do NOT need the
  // viewTransition prop — this router-level setting applies site-wide.
  defaultViewTransition: true,
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const rootElement = document.getElementById('root')
if (!rootElement) throw new Error('#root element not found')

const root = createRoot(rootElement)
root.render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <ThemeProvider>
        <RouterProvider router={router} />
        <AppToaster />
      </ThemeProvider>
    </QueryClientProvider>
  </StrictMode>,
)
initWebVitals()
